import { useEffect, useId, useRef, useState } from 'react';
import type { CheckMonitorResponse } from 'types/checkMonitor';
import type { MonitorDevice } from './series';
import { displayInstant, intervalLabel } from './calendar';
import { SeriesMarker } from './SeriesMarker';
import styles from './CheckMonitor.module.scss';

interface Point {
  serial: string;
  start: string;
}
interface Props {
  data: CheckMonitorResponse;
  devices: MonitorDevice[];
  fallbackFocus: () => void;
}
export const CheckMonitorChart = ({ data, devices, fallbackFocus }: Props) => {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<HTMLDivElement>(null);
  const wasFocused = useRef(false);
  const [width, setWidth] = useState(600);
  const [selected, setSelected] = useState<Point>();
  const [open, setOpen] = useState(false);
  const descriptionId = useId();
  const helpId = useId();
  const available = data.buckets.findIndex((bucket) => bucket.available);
  const deviceIndex = Math.max(
    0,
    devices.findIndex((device) => device.serialNumber === selected?.serial)
  );
  const foundBucket = data.buckets.findIndex(
    (bucket) => bucket.start === selected?.start
  );
  const bucketIndex = foundBucket >= 0 ? foundBucket : Math.max(0, available);
  const device = devices[deviceIndex];
  const bucket = data.buckets[bucketIndex];
  const hasPoints = !!device && available >= 0;

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(240, Math.floor(entry.contentRect.width)))
    );
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!hasPoints && wasFocused.current) {
      wasFocused.current = false;
      fallbackFocus();
    }
  }, [hasPoints, fallbackFocus]);
  useEffect(() => {
    if (
      hasPoints &&
      device &&
      bucket &&
      (selected?.serial !== device.serialNumber ||
        selected?.start !== bucket.start)
    ) {
      setSelected({ serial: device.serialNumber, start: bucket.start });
    }
  }, [hasPoints, device, bucket, selected]);
  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (
        !(event.target instanceof Element) ||
        !event.target.closest('[data-point]')
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const choose = (seriesIndex: number, intervalIndex: number) => {
    setSelected({
      serial: devices[seriesIndex].serialNumber,
      start: data.buckets[intervalIndex].start,
    });
    setOpen(true);
  };
  const max = Math.max(
    1,
    ...devices.flatMap((item) => item.counts.map((count) => count ?? 0))
  );
  const step = Math.max(1, Math.ceil(max / 4));
  const top = step * 4;
  const height = 250;
  const left = 44;
  const right = width - 24;
  const bottom = height - 36;
  const x = (index: number) =>
    left + (index * (right - left)) / Math.max(1, data.buckets.length - 1);
  const y = (count: number) => bottom - (count * (bottom - 24)) / top;
  const tickEvery = Math.max(
    1,
    Math.ceil(data.buckets.length / (width < 500 ? 4 : 8))
  );
  const count = device?.counts[bucketIndex];
  const pointText =
    device && bucket
      ? `${device.label}. Serie ${device.serialNumber}. ${intervalLabel(bucket.start, bucket.end, data.timeZone)}. ${count === null || !bucket.available ? (bucket.unavailableReason === 'future' ? 'Intervalo futuro; conteo no disponible.' : 'Sin cobertura; conteo no disponible.') : `${count ?? 0} checadas registradas.`}${bucket.inProgress ? ' En curso.' : ''}${bucket.partialCoverage ? ` Cobertura parcial desde ${displayInstant(data.coverageStart, data.timeZone, true)}.` : ''}`
      : '';

  if (!devices.length) return <div ref={container} />;

  return (
    <div ref={container} className={styles.chartArea}>
      <p id={helpId} className={styles.help}>
        Usa ← → para recorrer intervalos y ↑ ↓ para cambiar de dispositivo.
        También puedes tocar un punto.
      </p>
      <div
        ref={chart}
        className={styles.chart}
        tabIndex={hasPoints ? 0 : -1}
        role="application"
        aria-label="Gráfica de checadas por dispositivo"
        aria-describedby={`${helpId} ${descriptionId}`}
        data-cy="monitor-chart"
        onFocus={() => {
          wasFocused.current = true;
          setOpen(true);
        }}
        onBlur={() => {
          wasFocused.current = false;
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            event.preventDefault();
            return;
          }
          if (
            !hasPoints ||
            !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
              event.key
            )
          )
            return;
          event.preventDefault();
          const nextDevice = Math.max(
            0,
            Math.min(
              devices.length - 1,
              deviceIndex +
                (event.key === 'ArrowDown'
                  ? 1
                  : event.key === 'ArrowUp'
                    ? -1
                    : 0)
            )
          );
          const nextBucket = Math.max(
            0,
            Math.min(
              data.buckets.length - 1,
              bucketIndex +
                (event.key === 'ArrowRight'
                  ? 1
                  : event.key === 'ArrowLeft'
                    ? -1
                    : 0)
            )
          );
          if (nextDevice !== deviceIndex || nextBucket !== bucketIndex)
            choose(nextDevice, nextBucket);
        }}
      >
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
          data-cy="monitor-svg"
        >
          <text x={left} y={13} className={styles.axis}>
            Checadas registradas
          </text>
          {[0, 1, 2, 3, 4].map((tick) => (
            <g key={tick}>
              <line
                x1={left}
                x2={right}
                y1={y(tick * step)}
                y2={y(tick * step)}
                className={styles.grid}
              />
              <text
                x={left - 8}
                y={y(tick * step) + 4}
                textAnchor="end"
                className={styles.axis}
              >
                {tick * step}
              </text>
            </g>
          ))}
          {data.buckets.map((item, index) =>
            index % tickEvery === 0 || index === data.buckets.length - 1 ? (
              <text
                key={item.start}
                x={x(index)}
                y={height - 12}
                textAnchor={
                  index === data.buckets.length - 1 ? 'end' : 'middle'
                }
                className={styles.axis}
              >
                {new Intl.DateTimeFormat('es-MX', {
                  timeZone: data.timeZone,
                  ...(data.period === 'day'
                    ? { hour: '2-digit', hourCycle: 'h23' as const }
                    : { day: 'numeric', month: 'short' }),
                }).format(new Date(item.start))}
              </text>
            ) : null
          )}
          {devices.map((item, seriesIndex) => {
            let connected = false;
            const path = data.buckets
              .map((interval, index) => {
                const value = item.counts[index];
                if (!interval.available || value == null) {
                  connected = false;
                  return '';
                }
                const command = `${connected ? 'L' : 'M'}${x(index)},${y(value)}`;
                connected = true;
                return command;
              })
              .join(' ');
            return (
              <g
                key={item.serialNumber}
                data-series={item.serialNumber}
                data-style={`${item.color}/${item.marker}/${item.dash}`}
              >
                <path
                  d={path}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={2}
                  strokeDasharray={item.dash}
                  vectorEffect="non-scaling-stroke"
                />
                {data.buckets.map((interval, index) => {
                  const value = item.counts[index];
                  if (!interval.available || value == null) return null;
                  return (
                    <g
                      key={interval.start}
                      data-point={`${item.serialNumber}/${interval.start}`}
                      data-count={value}
                      fill={item.color}
                      stroke={item.color}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        chart.current?.focus();
                        choose(seriesIndex, index);
                      }}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse')
                          choose(seriesIndex, index);
                      }}
                    >
                      <SeriesMarker
                        marker={item.marker}
                        x={x(index)}
                        y={y(value)}
                      />
                      <circle
                        cx={x(index)}
                        cy={y(value)}
                        r={12}
                        fill="transparent"
                        stroke="none"
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}
          {open && hasPoints && bucket?.available && count != null && (
            <circle
              cx={x(bucketIndex)}
              cy={y(count)}
              r={7}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              pointerEvents="none"
            />
          )}
        </svg>
      </div>
      <div
        id={descriptionId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.tooltip}
        data-cy="monitor-tooltip"
      >
        {open && hasPoints
          ? pointText
          : 'Selecciona un punto para consultar su intervalo y conteo.'}
      </div>
    </div>
  );
};
